/**
 * Release 3.3, B4: product tiers of a discovery finding, as the screens show
 * them. The controller decides the tier (api-controller finding-tier.ts) and
 * sends it as `tier`; this file only names, explains and counts it.
 *
 *   Confirmed  validated and supported by its context; measured precision at
 *              the Confirmed target on the gold corpus.
 *   Likely     validated with partial context; measured at the Likely target.
 *   Possible   structure or a column name only, or a near-miss: listed and
 *              exportable, never in headline numbers.
 */
import type { FindingTierCounts } from './api';

export type FindingTier = 'confirmed' | 'likely' | 'possible';

export const HEADLINE_TIERS: readonly FindingTier[] = ['confirmed', 'likely'];

export function isHeadline(t: FindingTier): boolean {
  return t !== 'possible';
}

function isTier(v: unknown): v is FindingTier {
  return v === 'confirmed' || v === 'likely' || v === 'possible';
}

/**
 * The tier of a finding as received: the controller's `tier`, else the stored
 * `metadata.findingTier`, else the legacy rule the controller also uses for a
 * row written before 3.3 (the engine's review or OCR-unverified tier is
 * Possible, anything else Likely).
 */
export function tierOf(dto: { tier?: unknown; metadata?: unknown }): FindingTier {
  if (isTier(dto.tier)) return dto.tier;
  const meta = (dto.metadata && typeof dto.metadata === 'object' ? dto.metadata : {}) as Record<string, unknown>;
  if (isTier(meta.findingTier)) return meta.findingTier;
  const matched = Number(meta.matched_count);
  const sampled = Number(meta.samples_count);
  // weak: fewer than 1 in 10 of at least 10 sampled values (weak-match.ts)
  const weak = Number.isFinite(matched) && Number.isFinite(sampled) && sampled >= 10 && matched < sampled * 0.1;
  return meta.tier === 'review' || meta.tier === 'ocr_unverified' || weak ? 'possible' : 'likely';
}

export const TIER_LABEL: Record<FindingTier, string> = {
  confirmed: 'Confirmed',
  likely: 'Likely',
  possible: 'Possible',
};

/** Badge variants of the shared Badge component. */
export const TIER_BADGE: Record<FindingTier, 'success' | 'info' | 'warning'> = {
  confirmed: 'success',
  likely: 'info',
  possible: 'warning',
};

export const TIER_HINT: Record<FindingTier, string> = {
  confirmed: 'Validated and backed by its context. Counts in headline numbers.',
  likely: 'Validated with partial context. Counts in headline numbers.',
  possible: 'A pattern or a column name only, not enough to count. Listed and exported, not counted in the findings and dashboard headline numbers.',
};

/** Plain words for the reason the controller recorded (`tierBasis`). */
export function tierBasisText(basis: string | null | undefined): string | null {
  if (!basis) return null;
  if (basis === 'calibrated' || basis === 'calibrated_group') return 'tier set by precision measured on the development corpus';
  if (basis === 'default') return 'too few corpus cases to measure; the default for this evidence';
  if (basis === 'uncalibrated') return 'a data type the corpus does not cover; the default for this evidence';
  if (basis === 'weak_match') return 'fewer than 1 in 10 sampled values matched';
  if (basis === 'deterministic_check') return 'a planted canary was read';
  if (basis === 'unmeasured_check') return 'a security check, not a data-type detection; no measured tier';
  if (basis === 'edm') return 'exact match with your own reference data';
  if (basis === 'bank_account_ifsc') return 'an account label with an IFSC in the same record';
  if (basis === 'bank_account_label_only') return 'an account label or column name only; a bank account has no check digit';
  if (basis === 'legacy') return 'found before release 3.3; the next scan sets the measured tier';
  if (basis === 'no_label') return 'no data type recorded';
  if (basis === 'no_context_pan') return 'a PAN-shaped value with nothing near it naming a PAN; the shape alone is not enough to count';
  if (basis === 'no_context_card') return 'a card-shaped number with no card word or label near it; listed for review, not counted';
  const cap = /^cap_([a-z_]+)_(file|column)$/.exec(basis);
  if (cap) {
    return cap[1] === 'name' || cap[1] === 'address'
      ? 'a name or address is never confirmed without your own reference data'
      : 'in a file this data type needs a column vote to be confirmed';
  }
  return basis;
}

type SevTotals = { all: number; critical: number; high: number; medium: number; low: number };

/** Totals per severity of one {status: {severity: n}} map, for the chosen status (or every status). */
export function severityTotals(byStatus: Record<string, Record<string, number>>, status?: string): SevTotals {
  const out: SevTotals = { all: 0, critical: 0, high: 0, medium: 0, low: 0 };
  const statuses = status ? [status] : Object.keys(byStatus);
  for (const st of statuses) {
    for (const [sev, n] of Object.entries(byStatus[st] ?? {})) {
      out.all += n;
      if (sev === 'critical' || sev === 'high' || sev === 'medium' || sev === 'low') out[sev] += n;
    }
  }
  return out;
}

/** Open = open, in progress or escalated (fleet/service.ts OPEN_DEFINITION). */
export const OPEN_STATUSES = ['open', 'in_progress', 'escalated'] as const;

export function openTotals(byStatus: Record<string, Record<string, number>>): SevTotals {
  const out: SevTotals = { all: 0, critical: 0, high: 0, medium: 0, low: 0 };
  for (const st of OPEN_STATUSES) {
    const t = severityTotals(byStatus, st);
    out.all += t.all;
    out.critical += t.critical;
    out.high += t.high;
    out.medium += t.medium;
    out.low += t.low;
  }
  return out;
}

/** The {status: {severity: n}} map of one lane: headline (Confirmed + Likely) or Possible. */
export function laneCounts(c: FindingTierCounts, possibleLane: boolean): Record<string, Record<string, number>> {
  return possibleLane ? c.byTier.possible : c.headline;
}
